import { useEffect, useMemo } from 'react'

import type { LabeledOption } from '@fv/client-components/combobox'

import InfoBox from '@/components/InfoBox'
import { InputGroupWrapper } from '@/components/inputs/InputGroup'
import { ShipperTypeahead } from '@/components/inputs/ShipperTypeahead'
import {
  useSpotQuoteCarriers,
  useSpotQuoteContacts,
} from '@/features/spot-quote-contacts/queries'

import { useBookingForm } from './hooks'
import { customerRoutedCarrierKey } from './types'

const ALL_CONTACTS_VALUE = '__all_contacts__'

type ContactOption = LabeledOption<string>

export const BookingFormCustomerRouted = () => {
  const { customerRoutedData, setCustomerRoutedData, setFormSections } =
    useBookingForm()

  const { carrierId, contactEmail } = customerRoutedData

  const carriersQuery = useSpotQuoteCarriers()
  const contactsQuery = useSpotQuoteContacts()

  const carrierOptions = useMemo(
    () =>
      (carriersQuery.data ?? [])
        .filter(c => c.supportsTLFreightCollect)
        .map(c => ({
          label: c.carrierName?.trim() ?? '',
          value: c.carrierId ?? '',
        })),
    [carriersQuery.data],
  )

  const selectedCarrier = carrierOptions.find(o => o.value === carrierId)

  const contactOptions = useMemo((): ContactOption[] => {
    const allContactsOption: ContactOption = {
      label: 'All contacts',
      value: ALL_CONTACTS_VALUE,
    }

    if (!carrierId) {
      return [allContactsOption]
    }

    const contacts = (contactsQuery.data ?? []).filter(
      c => c.carrierId === carrierId && c.email,
    )

    return [
      allContactsOption,
      ...contacts.map(c => ({
        label: c.email,
        value: c.email,
      })),
    ]
  }, [contactsQuery.data, carrierId])

  const selectedContact =
    contactOptions.find(option =>
      contactEmail
        ? option.value === contactEmail
        : option.value === ALL_CONTACTS_VALUE,
    ) ?? contactOptions[0]

  useEffect(() => {
    if (!carrierId) return
    setFormSections(prev =>
      prev.map(s =>
        s.key === customerRoutedCarrierKey ? { ...s, isValid: true } : s,
      ),
    )
  }, [carrierId, setFormSections])

  return (
    <InfoBox custom className="mb-4 mt-4">
      <div className="flex items-center gap-2">
        <InputGroupWrapper
          label="Carrier"
          name="cr-carrier"
          required
          className="w-1/4"
        >
          <ShipperTypeahead
            showTrigger
            classNames={{ container: 'w-full max-w-md' }}
            getOptionValue={c => c.value}
            isLoading={carriersQuery.isLoading}
            name="cr-carrier"
            onSelect={(option: LabeledOption<string> | null) => {
              setCustomerRoutedData(prev => ({
                ...prev,
                carrierId: option?.value ?? null,
                contactEmail: null,
              }))
            }}
            options={carrierOptions}
            required
            value={selectedCarrier ?? null}
          />
        </InputGroupWrapper>

        <InputGroupWrapper
          className="w-3/4"
          label="Contact"
          name="cr-carrier-contact"
          required
        >
          <ShipperTypeahead
            showTrigger
            classNames={{ container: 'w-full max-w-md' }}
            disabled={!carrierId}
            getOptionValue={c => c.value}
            isLoading={contactsQuery.isLoading}
            name="cr-carrier-contact"
            onSelect={(option: ContactOption | null) => {
              setCustomerRoutedData(prev => ({
                ...prev,
                contactEmail:
                  !option || option.value === ALL_CONTACTS_VALUE
                    ? null
                    : option.value,
              }))
            }}
            options={contactOptions}
            value={selectedContact ?? null}
          />
        </InputGroupWrapper>
      </div>
    </InfoBox>
  )
}
